import { Row, TablePaginationConfig, Tooltip } from 'antd';
import React, { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { CONFIG } from 'utils/constants';
import { getTaskStatusText, toFixedIfFloat } from 'utils/helpers';

import moment from 'moment';
import {
  ITasksTableItem,
  TaskListSort,
  taskListTableColEnum,
  taskStatusEnum,
} from 'app/containers/Task/slice/types';
import * as S from './TasksTable.styles';
import { testIdEnum } from 'types';
import { getTaskCulturesString } from 'app/containers/Task/slice/helpers';
import { ColumnsType } from 'antd/lib/table';
import { SorterResult } from 'antd/lib/table/interface';
import { StyledTable } from '../StyledTable/StyledTable';

interface IProps {
  tasksArr: ITasksTableItem[];
  onRowClick: (taskId: number) => () => void;
  onPageChange: (page: number) => void;
  currentPage: number;
  total: number;
  loading: boolean;
  setSort: (payload: TaskListSort) => void;
}

const TasksTable: FC<IProps> = props => {
  const {
    tasksArr,
    onRowClick,
    currentPage,
    total,
    loading,
    onPageChange,
    setSort,
  } = props;

  const { t } = useTranslation();

  const renderUsedArea = useCallback(
    (total: number, completed: number) => {
      const totalArea = toFixedIfFloat(total);
      const usedArea = toFixedIfFloat(completed);

      if (totalArea !== usedArea) {
        return (
          <Tooltip
            title={t(
              'Task.ListTable.UsedAreaTotalArea',
              'Used area / Total area',
            )}
          >{`${usedArea} / ${totalArea}`}</Tooltip>
        );
      } else return totalArea;
    },
    [t],
  );

  const columns: ColumnsType<ITasksTableItem> = [
    {
      title: t('Task.ListTable.ID', 'ID'),
      dataIndex: taskListTableColEnum.id,
      key: taskListTableColEnum.id,
      sorter: true,
    },
    {
      title: t('Task.ListTable.Name', 'Name'),
      dataIndex: taskListTableColEnum.name,
      key: taskListTableColEnum.name,
      sorter: true,
    },
    {
      title: t('Task.ListTable.Category', 'Category'),
      dataIndex: ['category', 'name'],
      key: taskListTableColEnum.category,
      sorter: true,
    },
    {
      title: t('Task.ListTable.Cultures', 'Cultures'),
      dataIndex: taskListTableColEnum.cultures,
      key: taskListTableColEnum.cultures,
      render: (cultures: string[]) => getTaskCulturesString(cultures),
      sorter: false,
    },

    {
      title: t('Task.ListTable.PlannedCompletion', 'Planned completion'),
      dataIndex: taskListTableColEnum.endDate,
      key: taskListTableColEnum.endDate,
      render: (endDate: string, task: ITasksTableItem) => {
        const status = task.status;
        const today = moment();
        const endDateMoment = moment(endDate);
        const isTaskLate = endDateMoment.isBefore(today, 'day');
        const daysDiff = moment(today).diff(endDateMoment, 'day');
        return (
          <Row
            wrap={false}
            data-testid={testIdEnum.taskPlannedCompletionColumn}
          >
            {endDate}
            {isTaskLate && taskStatusEnum.completed !== status && (
              <Tooltip
                title={t('Task.ListTable.TaskDaysLate', {
                  defaultValue: '{{count}} days late',
                  count: daysDiff,
                })}
              >
                <S.WarningCircle
                  data-testid={testIdEnum.taskLateWaningCircle}
                />
              </Tooltip>
            )}
          </Row>
        );
      },

      sorter: true,
    },
    {
      title: t('Task.ListTable.Area', 'Area (ha)'),
      dataIndex: taskListTableColEnum.area,
      key: taskListTableColEnum.area,
      render: (_: unknown, task: ITasksTableItem) =>
        renderUsedArea(task.totalArea, task.completedArea),
      sorter: true,
    },
    {
      title: t('Task.ListTable.Status', 'Status'),
      dataIndex: taskListTableColEnum.status,
      key: taskListTableColEnum.status,
      render: (_: unknown, task: ITasksTableItem) => {
        const status = task.status;
        return (
          <>
            <Tooltip title={getTaskStatusText(status)} placement="right">
              <S.StatusCircleIcon
                data-testid={testIdEnum.taskStatusCircle}
                data-status={status}
                $status={status}
              />
            </Tooltip>
          </>
        );
      },
      sorter: true,
    },
  ];

  const onTableChange = (
    pagination: TablePaginationConfig,
    filters: unknown,
    sorter: SorterResult<ITasksTableItem> | SorterResult<ITasksTableItem>[],
  ) => {
    if ('field' in sorter && sorter.field && sorter.order) {
      const { field, order } = sorter;
      console.log('field:', field);
      console.log('order:', order);

      let name = field;
      if (Array.isArray(field)) {
        name = field.join('.');
      }

      setSort({
        name: String(name),
        order: order === 'ascend' ? 'asc' : 'desc',
      });
    } else {
      setSort(null);
    }
  };

  return (
    <StyledTable
      data-testid={'tasksTable'}
      style={{ width: '100%' }}
      loading={loading}
      rowKey={task => task.id}
      dataSource={tasksArr}
      columns={columns}
      pagination={{
        onChange: onPageChange,
        showTotal: total =>
          t('Task.ListTable.TotalPages', {
            defaultValue: 'Total: {{total}}',
            total,
          }),
        current: currentPage,
        total: total,
        pageSize: CONFIG.TABLE_PAGE_SIZE,
      }}
      onRow={task => {
        return {
          onClick: onRowClick(task.id),
        };
      }}
      onChange={onTableChange}
    />
  );
};

export default TasksTable;
